import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import AvatarWithFlag from 'components/AvatarWithFlag/AvatarWithFlag';
import Points from 'components/Points';
import UsernameLink from 'components/UsernameLink/UsernameLink';
import { useGetPublicProfileQuery } from 'store/services/profile/profileApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import {
  useAcceptTradeOfferMutation,
  useCancelTradeOfferMutation,
} from 'store/services/tradeOffers/tradeOffersApi';
import { TradeOffer, TradeOfferStatus } from 'types/interfaces/tradeOffer';
import {
  canFundInbound,
  getFundingMessage,
  getFundingOutcome,
  getNetCopy,
  OFFER_CARDS_NOT_HELD_NOTICE,
  OFFER_PRICE_LOCK_NOTICE,
  OFFER_SEQUENCED_REASSURANCE,
} from 'utils/fundingCopy';
import { ParsedOfferError, parseOfferError } from 'utils/offerErrors';
import { useExpiryCountdown } from 'hooks/useExpiryCountdown';
import { DeferredDeclineApi } from '../hooks/useDeferredDecline';
import OfferCardRow from './OfferCardRow';
import './OfferSummaryCard.css';

const STATUS_LABELS: Record<TradeOfferStatus, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
  cancelled: 'Cancelled',
  failed: 'Failed',
};

interface OfferSummaryCardProps {
  offer: TradeOffer;
  tab: 'received' | 'sent';
  /** Zero-state sample mode: no live countdown, no actions, no hover card */
  isSample?: boolean;
  /** Deep-link target: briefly outlined after a scroll-to */
  highlight?: boolean;
  /** Supplied by TradeOffersPage; absent in sample mode */
  deferredDecline?: DeferredDeclineApi;
}

/**
 * An offer is a proposition to judge AND the place it's decided. Header carries
 * the trust block; the body is a 1fr 64px 1fr grid — THEY SEND YOU (violet,
 * inbound) on the left, YOU SEND THEM (magenta, outbound) on the right — and the
 * footer states the net in words, the escrow outcome, and the price lock, then
 * ends in Decline / Accept offer. There is no review page: every disclosure
 * that lived there renders here, next to the buttons, so nothing can be skipped.
 */
const OfferSummaryCard: React.FC<OfferSummaryCardProps> = ({
  offer,
  tab,
  isSample = false,
  highlight = false,
  deferredDecline,
}) => {
  const navigate = useNavigate();
  const [acceptOffer, { isLoading: isAccepting }] = useAcceptTradeOfferMutation();
  const [cancelOffer, { isLoading: isCancelling }] = useCancelTradeOfferMutation();
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  // Terminal accept failure (409): rendered in place of the actions, no retry
  const [acceptFailure, setAcceptFailure] = useState<ParsedOfferError | null>(null);
  // Non-terminal accept problems (400 funding / 503 transient): inline, actions stay
  const [acceptNotice, setAcceptNotice] = useState<string | null>(null);
  // Success latch: keeps the button disabled between the 200 and the refetch
  const [acceptSucceeded, setAcceptSucceeded] = useState(false);

  const locallyDeclined = !isSample && !!deferredDecline?.isLocallyDeclined(offer.id);
  const effectiveStatus: TradeOfferStatus = locallyDeclined ? 'declined' : offer.status;

  const counterparty = tab === 'received' ? offer.proposer : offer.recipient;
  const youSendTotal =
    tab === 'received' ? offer.totals.recipientSendsPoints : offer.totals.proposerSendsPoints;
  const theySendTotal =
    tab === 'received' ? offer.totals.proposerSendsPoints : offer.totals.recipientSendsPoints;
  const youSendItems = offer.items.filter((i) =>
    tab === 'received' ? i.direction === 'RECIPIENT_SENDS' : i.direction === 'PROPOSER_SENDS'
  );
  const theySendItems = offer.items.filter((i) =>
    tab === 'received' ? i.direction === 'PROPOSER_SENDS' : i.direction === 'RECIPIENT_SENDS'
  );

  const { netLine, whyLine, netDirection, netAmount, netPrefix } = getNetCopy(
    youSendTotal,
    theySendTotal
  );
  const countdown = useExpiryCountdown(offer.expiresAt, !isSample && effectiveStatus === 'pending');

  const isPendingReceived = !isSample && tab === 'received' && effectiveStatus === 'pending';

  // Escrow outcome needs both balances. The viewer's rides the app-wide getMe
  // cache; the counterparty's is public on their profile. Fetched only for
  // pending received rows (the only rows with an Accept button) and deduped by
  // RTK across rows sharing a counterparty — no funding data arrives in the
  // offer payload today.
  const { data: me } = useGetMeQuery(undefined, { skip: !isPendingReceived });
  const { data: counterpartyProfile } = useGetPublicProfileQuery(counterparty.username, {
    skip: !isPendingReceived,
  });

  const fundingKnown = isPendingReceived && !!me && !!counterpartyProfile;
  const viewerCanFund = fundingKnown ? canFundInbound(me.points, theySendTotal) : true;
  const counterpartyCanFund = fundingKnown
    ? canFundInbound(counterpartyProfile.points, youSendTotal)
    : true;
  const fundingOutcome = getFundingOutcome(viewerCanFund, counterpartyCanFund);
  const fundingMessage = fundingKnown
    ? getFundingMessage({
        viewerCanFund,
        counterpartyCanFund,
        counterpartyUsername: counterparty.username,
      })
    : null;
  // The second leg opens automatically — reassure, never warn
  const showSequencedReassurance =
    fundingOutcome === 'viewer_ships_first' || fundingOutcome === 'counterparty_ships_first';

  // Synchronous double-fire guard: two clicks can land in one React batch
  // before the mutation's isLoading disables the button.
  const acceptInFlightRef = useRef(false);

  const handleAccept = async () => {
    if (acceptInFlightRef.current) return;
    acceptInFlightRef.current = true;
    setAcceptNotice(null);
    try {
      await acceptOffer(offer.id).unwrap();
      // No navigation: the mutation invalidates the list and the row re-renders
      // as Accepted with its View trades button. The guard stays LATCHED on
      // success — a fast backend can complete the accept inside a double-click
      // gap, before the refetch replaces the button, and an accepted offer can
      // never be accepted twice.
      setAcceptSucceeded(true);
      toast.success('Offer accepted. Both trades have been created.');
    } catch (err) {
      const parsed = parseOfferError(err);
      if (parsed.terminal) {
        // 409: the offer is cancelled server-side, no trade was created, no retry
        setAcceptFailure(parsed);
      } else {
        // 400 funding shortfall (offer stays pending) or 503 transient
        setAcceptNotice(parsed.message);
      }
      acceptInFlightRef.current = false;
    }
  };

  const handleDecline = () => {
    deferredDecline?.declineWithUndo(offer);
  };

  const handleCancel = async () => {
    if (!confirmingCancel) {
      setConfirmingCancel(true);
      return;
    }
    try {
      await cancelOffer(offer.id).unwrap();
      toast.info('Offer cancelled.');
    } catch (err) {
      toast.error(parseOfferError(err).message);
      setConfirmingCancel(false);
    }
  };

  return (
    <article
      id={isSample ? undefined : `offer-${offer.id}`}
      className={`offer-summary-card${highlight ? ' offer-summary-card--highlight' : ''}`}
    >
      <header className="offer-summary-header">
        <AvatarWithFlag
          avatarUrl={counterparty.avatarUrl}
          username={counterparty.username}
          size={36}
          isElite={counterparty.isElite}
          showAnimatedBorder={counterparty.showAnimatedBorder}
          borderColor1={counterparty.borderColor1}
          borderColor2={counterparty.borderColor2}
          alt=""
        />
        <div className="offer-summary-who">
          <UsernameLink
            username={counterparty.username}
            isElite={counterparty.isElite}
            showHoverCard={!isSample}
            as={isSample ? 'span' : 'link'}
          />
          <span className="offer-summary-trust">
            {counterparty.stats.rating != null
              ? `★ ${counterparty.stats.rating.toFixed(1)} (${counterparty.stats.ratingCount})`
              : 'No ratings yet'}
            {` · ${counterparty.stats.cardsSent.toLocaleString()} cards sent`}
          </span>
        </div>
        <div className="offer-summary-header-right">
          {countdown && (
            <span className="offer-summary-expiry">
              {countdown === 'expired' ? 'Expired' : `Expires in ${countdown}`}
            </span>
          )}
          <span className={`offers-status-chip offer-status--${effectiveStatus}`}>
            {STATUS_LABELS[effectiveStatus]}
          </span>
        </div>
      </header>

      <div className="offer-summary-body">
        <section className="offer-summary-side offer-summary-side--they" aria-label="They send you">
          <div className="offer-summary-side-label offer-summary-side-label--they">
            THEY SEND YOU
          </div>
          {theySendItems.map((item) => (
            <OfferCardRow
              key={item.id}
              card={item.card}
              foil={item.foil}
              condition={item.condition}
              pricePoints={item.lockedPricePoints}
              quantityBadge={item.quantity}
            />
          ))}
          <div className="offer-summary-side-total">
            <Points type="small" points={theySendTotal} />
          </div>
        </section>

        <div className="offer-summary-swap-axis" aria-hidden="true">
          <span className="offer-summary-swap-glyph">⇄</span>
        </div>

        <section className="offer-summary-side offer-summary-side--you" aria-label="You send them">
          <div className="offer-summary-side-label offer-summary-side-label--you">YOU SEND THEM</div>
          {youSendItems.map((item) => (
            <OfferCardRow
              key={item.id}
              card={item.card}
              foil={item.foil}
              condition={item.condition}
              pricePoints={item.lockedPricePoints}
              quantityBadge={item.quantity}
            />
          ))}
          <div className="offer-summary-side-total">
            <Points type="small" points={youSendTotal} />
          </div>
        </section>
      </div>

      <footer className="offer-summary-footer">
        <div className="offer-summary-net-block">
          {netAmount != null ? (
            <span className="offer-summary-net-row" aria-label={netLine}>
              <span className={`offer-summary-net offer-summary-net--${netDirection}`}>
                {netPrefix}
              </span>
              <Points type="big" points={netAmount} />
            </span>
          ) : (
            <span className="offer-summary-net">{netLine}</span>
          )}
          {whyLine && <span className="offer-summary-why">{whyLine}</span>}
        </div>

        {isPendingReceived && !acceptFailure && (
          // Every disclosure the old review page carried, next to the buttons:
          // the escrow outcome, the sequencing reassurance, the price lock, and
          // the not-held expectation. Visible without any expand or hover.
          <div className="offer-summary-callout">
            {fundingMessage && (
              <div className="offer-summary-callout-message">{fundingMessage}</div>
            )}
            {showSequencedReassurance && (
              <div className="offer-summary-callout-reassure">{OFFER_SEQUENCED_REASSURANCE}</div>
            )}
            <div className="offer-summary-callout-lock">{OFFER_PRICE_LOCK_NOTICE}</div>
            <div className="offer-summary-callout-expectation">{OFFER_CARDS_NOT_HELD_NOTICE}</div>
          </div>
        )}

        {/* The sample's caption already states the lock; pending received rows
            state it inside the callout instead */}
        {!isSample && !isPendingReceived && (
          <div className="offer-summary-lock">{OFFER_PRICE_LOCK_NOTICE}</div>
        )}

        {acceptFailure ? (
          <div className="offer-summary-failure" role="alert">
            <h2>This offer couldn&apos;t be accepted</h2>
            <p>{acceptFailure.message}</p>
            <p>
              If you still want to trade, ask {counterparty.username} to build a new offer.
            </p>
          </div>
        ) : !isSample &&
          (isPendingReceived ||
            (tab === 'sent' && effectiveStatus === 'pending') ||
            effectiveStatus === 'accepted') ? (
          <div className="offer-summary-actions-block">
            {acceptNotice && (
              <div className="offer-summary-notice" role="status">
                {acceptNotice}
              </div>
            )}
            <div className="offer-summary-actions">
              {isPendingReceived && (
                <>
                  <button
                    type="button"
                    className="offer-summary-decline"
                    disabled={isAccepting || acceptSucceeded}
                    onClick={handleDecline}
                  >
                    Decline
                  </button>
                  <button
                    type="button"
                    className="offer-summary-accept"
                    disabled={isAccepting || acceptSucceeded}
                    onClick={handleAccept}
                  >
                    {isAccepting ? 'Accepting…' : 'Accept offer'}
                  </button>
                </>
              )}
              {tab === 'sent' && effectiveStatus === 'pending' && (
                <button
                  type="button"
                  className="offers-cancel-btn"
                  disabled={isCancelling}
                  onClick={handleCancel}
                >
                  {isCancelling ? 'Cancelling…' : confirmingCancel ? 'Confirm cancel' : 'Cancel'}
                </button>
              )}
              {effectiveStatus === 'accepted' && (
                <button
                  type="button"
                  className="offers-cancel-btn"
                  onClick={() => navigate(tab === 'received' ? '/receiving' : '/sending')}
                >
                  View trades
                </button>
              )}
            </div>
          </div>
        ) : null}
      </footer>
    </article>
  );
};

export default OfferSummaryCard;
